import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BlogPost } from '@/types/blog';

interface BlogTimelineSidebarProps {
  posts: BlogPost[];
}

export default function BlogTimelineSidebar({ posts }: BlogTimelineSidebarProps) {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';

  // Sort by published_at descending (latest first)
  const sorted = [...posts].sort((a, b) => {
    const da = a.published_at ? new Date(a.published_at).getTime() : 0;
    const db = b.published_at ? new Date(b.published_at).getTime() : 0;
    return db - da;
  });

  return (
    <div className="h-full overflow-y-auto py-24 px-2 blog-timeline-scrollbar">
      <div className="relative flex flex-col items-center">
        {/* Vertical line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/15 -translate-x-px" />

        {sorted.map((post) => {
          const title = isZh && post.title_zh ? post.title_zh : post.title;
          const dateStr = post.published_at
            ? new Date(post.published_at).toLocaleDateString(isZh ? 'zh-TW' : 'en-US', {
                month: 'short',
                day: 'numeric',
              })
            : '';

          return (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="relative group flex items-center justify-center py-5 w-full"
            >
              {/* Circle */}
              <div className="w-3 h-3 rounded-full bg-[var(--cyan-electric)]/60 border-2 border-[var(--navy-deep)] group-hover:bg-[var(--cyan-electric)] group-hover:scale-150 group-hover:shadow-[0_0_12px_var(--cyan-glow)] transition-all duration-200 z-10 flex-shrink-0" />

              {/* Date label below circle */}
              <span className="absolute top-[calc(50%+12px)] left-1/2 -translate-x-1/2 text-[10px] text-gray-500 whitespace-nowrap">
                {dateStr}
              </span>

              {/* Tooltip - appears to the right */}
              <div className="absolute left-full ml-4 top-1/2 -translate-y-1/2 hidden group-hover:block z-50 pointer-events-none">
                <div className="bg-[var(--navy-mid)] border border-white/15 rounded-lg px-3 py-2 shadow-xl max-w-[200px]">
                  <p className="text-white text-sm font-medium leading-tight line-clamp-2">{title}</p>
                  {post.category && (
                    <span className="text-[10px] text-[var(--amber-warm)] uppercase tracking-wider mt-1 block">
                      {post.category}
                    </span>
                  )}
                </div>
                {/* Arrow pointing left */}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent border-r-[var(--navy-mid)]" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
